import { ClockCircleOutlined, DownOutlined, UpOutlined } from '@ant-design/icons';
import { IssueTaskDoc, IssueTaskStatus, issueTaskCategoryMap } from '@gooduncles/gu-app-schema';
import { Popover, Tag } from 'antd';
import dayjs from 'dayjs';
import { FC, useState } from 'react';

import { IssueInfoItem } from 'src/atomic-components/atoms/IssueInfoItem/IssueInfoItem';
import IssueAmount from 'src/atomic-components/molecules/IssueAmount/IssueAmount';
import { IssueProductItem } from 'src/atomic-components/molecules/IssueProductItem/IssueProductItem';
import IssueTaskDeliveryInfo from 'src/atomic-components/molecules/IssueTaskCard/IssueTaskDeliveryInfo';
import IssueTaskInventoryIssueInfo from 'src/atomic-components/molecules/IssueTaskCard/IssueTaskInventoryIssueInfo';
import { issueTaskStatusBadgeMap } from 'src/utils/issue/issue-util';

import classes from './TaskSummaryCard.module.scss';

interface TaskSummaryCardProps {
  task: IssueTaskDoc;
}

const TaskSummaryCard: FC<TaskSummaryCardProps> = ({ task }) => {
  const [expanded, setExpanded] = useState(false);
  const statusInfo = issueTaskStatusBadgeMap[task.status];
  const categoryLabel = issueTaskCategoryMap[task.category];

  const isUrgent =
    task.targetDate &&
    task.status < IssueTaskStatus.COMPLETED &&
    task.status !== IssueTaskStatus.CANCELED &&
    dayjs(task.targetDate).isBefore(dayjs(), 'day');

  const toggleExpand = () => setExpanded((prev) => !prev);

  return (
    <div className={classes.card}>
      <div className={classes.header} onClick={toggleExpand}>
        <div className={classes.category}>
          <Popover title='Task ID' content={task._id}>
            <span className={classes.categoryText}>{categoryLabel}</span>
          </Popover>
          {task.targetDate ? (
            <span className={`${classes.targetDate} ${isUrgent ? classes.urgent : ''}`}>
              {isUrgent && <ClockCircleOutlined />} {dayjs(task.targetDate).format('MM.DD')}
            </span>
          ) : (
            <span className={classes.targetDate}>미정</span>
          )}
        </div>
        <div className={classes.rightSection}>
          <Tag color={statusInfo.status} style={{ marginRight: 0 }}>
            {statusInfo.text}
          </Tag>
          <div className={classes.expandIcon}>{expanded ? <UpOutlined /> : <DownOutlined />}</div>
        </div>
      </div>

      {expanded && (
        <div className={classes.details}>
          <IssueInfoItem label='등록 시각' value={dayjs(task.registeredAt).format('YY.MM.DD HH:mm')} />

          {task.finishedAt && (
            <IssueInfoItem label='처리 시각' value={dayjs(task.finishedAt).format('YY.MM.DD HH:mm')} />
          )}

          {(task.publicNote || task.privateNote) && (
            <>
              {task.publicNote && <IssueInfoItem label='공개 메모' value={task.publicNote} />}
              {task.privateNote && <IssueInfoItem label='비공개 메모' value={task.privateNote} />}
            </>
          )}

          {task.products && task.products.length > 0 && (
            <div className={classes.section}>
              <IssueInfoItem label='처리 상품' value={task.products.length + '건'} />
              <div className={classes.productList}>
                {task.products.map((product, index) => (
                  <IssueProductItem
                    key={task._id + product.productId}
                    productName={product.productName}
                    unitPrice={product.price}
                    quantity={product.quantity}
                    totalPrice={product.price * product.quantity}
                    isLast={index === (task.products?.length ?? 0) - 1}
                    showHierarchy={false}
                  />
                ))}
              </div>
            </div>
          )}

          {task.amount && (
            <div className={classes.section} style={{ padding: 0, borderTop: 'none' }}>
              <IssueAmount issueProducts={task.products ?? []} issueAmount={task.amount} />
            </div>
          )}

          {task.deliveryInfo && (
            <div className={classes.detailSection}>
              <IssueTaskDeliveryInfo issueTaskDoc={task} deliveryInfo={task.deliveryInfo} disabled={true} />
            </div>
          )}

          {task.inventoryIssueInfo && (
            <div className={classes.detailSection}>
              <IssueTaskInventoryIssueInfo issueTaskDoc={task} />
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default TaskSummaryCard;
